/**
 * Cliente HTTP único del admin. Sesión por cookie httpOnly (`credentials: 'include'`) + header `x-coreai-csrf`
 * en toda mutación (el token sale de `GET /admin/auth/me` o del login). Errores tipados `{error:{code,message}}`.
 * Un 401 (salvo en el login) avisa a `onUnauthorized` (el router redirige a /login).
 */

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message;
  if (e instanceof Error) return e.message;
  return 'Ocurrió un error inesperado';
}

function isErrorBody(v: unknown): v is ApiErrorBody {
  if (typeof v !== 'object' || v === null || !('error' in v)) return false;
  const err = (v as { error: unknown }).error;
  return typeof err === 'object' && err !== null && 'code' in err && 'message' in err;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Archivo CRUDO como cuerpo (su `type` va como `Content-Type`); excluye `body`. */
  raw?: Blob | undefined;
  query?: Query | undefined;
  signal?: AbortSignal | undefined;
}

export interface ApiClientOptions {
  fetchImpl?: typeof fetch;
  baseUrl?: string;
  onUnauthorized?: () => void;
}

const LOGIN_PATH = '/admin/auth/login';
/** Rutas públicas (sin sesión ni token CSRF: el login, la recuperación de contraseña, la aceptación de una invitación con cuenta nueva y el registro por autoservicio). */
const PUBLIC_PATHS = new Set([
  LOGIN_PATH,
  '/admin/auth/forgot-password',
  '/admin/auth/reset-password',
  '/admin/auth/invitations/preview',
  '/admin/auth/invitations/accept',
  // Registro por autoservicio (PR-11) y catálogo público de planes.
  '/admin/signup/plans',
  '/admin/signup/request',
  '/admin/signup/preview',
  '/admin/signup/verify',
  // Enlace de la persona para aceptar o rechazar otra hora (ADR-0021): sin sesión.
  '/admin/appointments/preview',
  '/admin/appointments/respond',
  // Pago simulado de entradas (ADR-0022): sin sesión.
  '/admin/payments/preview',
  '/admin/payments/respond',
  // Confirmación de una reserva de mesa (ADR-0024): sin sesión.
  '/admin/reservations/preview',
  '/admin/reservations/respond',
  // Valoración posterior a la experiencia (ADR-0027): sin sesión.
  '/admin/feedback/preview',
  '/admin/feedback/respond',
]);
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export function createApiClient(options: ApiClientOptions = {}) {
  const baseUrl = options.baseUrl ?? '';
  let csrfToken: string | null = null;
  let csrfInflight: Promise<string> | null = null;
  let onUnauthorized = options.onUnauthorized;

  const doFetch: typeof fetch = (...args) => (options.fetchImpl ?? fetch)(...args);

  const buildUrl = (path: string, query?: Query): string => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    }
    const qs = params.toString();
    return `${baseUrl}${path}${qs ? `?${qs}` : ''}`;
  };

  async function parse<T>(res: Response): Promise<T> {
    const text = await res.text();
    let json: unknown;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = undefined;
      }
    }
    if (!res.ok) {
      if (isErrorBody(json)) throw new ApiError(res.status, json.error.code, json.error.message, json.error.details);
      throw new ApiError(res.status, 'http_error', `Error ${res.status} del servidor`);
    }
    return json as T;
  }

  /** Pide el CSRF vigente a `/admin/auth/me` (una sola petición concurrente). */
  async function loadCsrf(): Promise<string> {
    csrfInflight ??= (async () => {
      const res = await doFetch(buildUrl('/admin/auth/me'), {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (res.status === 401) onUnauthorized?.();
      const data = await parse<{ csrfToken: string }>(res);
      csrfToken = data.csrfToken;
      return data.csrfToken;
    })().finally(() => {
      csrfInflight = null;
    });
    return csrfInflight;
  }

  async function request<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
    const method = opts.method ?? 'GET';
    const headers: Record<string, string> = { accept: 'application/json' };
    if (opts.raw) headers['content-type'] = opts.raw.type;
    else if (opts.body !== undefined) headers['content-type'] = 'application/json';
    // El login y la recuperación de contraseña son públicos (aún no hay sesión ni token CSRF que pedir).
    if (!SAFE_METHODS.has(method) && !PUBLIC_PATHS.has(path))
      headers['x-coreai-csrf'] = csrfToken ?? (await loadCsrf());

    const res = await doFetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'include',
      signal: opts.signal ?? null,
      body: opts.raw ?? (opts.body === undefined ? null : JSON.stringify(opts.body)),
    });

    try {
      return await parse<T>(res);
    } catch (e) {
      if (isApiError(e)) {
        if (e.status === 401 && !PUBLIC_PATHS.has(path)) {
          csrfToken = null;
          onUnauthorized?.();
        } else if (e.status === 403 && e.code === 'csrf_invalid' && !retried) {
          csrfToken = null;
          await loadCsrf();
          return request<T>(path, opts, true);
        }
      }
      throw e;
    }
  }

  return {
    request,
    get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>(path, { query, signal }),
    post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
    postRaw: <T>(path: string, raw: Blob, query?: Query) => request<T>(path, { method: 'POST', raw, query }),
    put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
    patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
    del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
    setCsrf(token: string | null) {
      csrfToken = token;
    },
    getCsrf: () => csrfToken,
    setOnUnauthorized(fn: (() => void) | undefined) {
      onUnauthorized = fn;
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export const api: ApiClient = createApiClient();
